import { useMemo, useCallback } from 'react';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import type { AggregatedStyleValues } from '../abstractStyles/types';

type CssPropertyType =
  (typeof CSS_PROPERTIES.CSS_PROPERTY_TYPE)[keyof typeof CSS_PROPERTIES.CSS_PROPERTY_TYPE];

type ProcessedStyleItem<TParsedValue> = {
  styleItemId: string;
  parsedValue: TParsedValue;
};

type BaseConfig = {
  /**
   * Aggregated style values from the engine
   */
  values: AggregatedStyleValues;
  /**
   * CSS property type(s) to filter by (e.g., 'boxShadow', 'filter')
   */
  cssPropertyTypes: CssPropertyType | readonly CssPropertyType[];

  /**
   * Function to update styles in the engine
   */
  updateStyles: (styles: Record<string, string>) => Promise<void>;
};

export type UsePartialStyleUpdateConfigWithKey<TParsedValue> = BaseConfig & {
  /**
   * Parse function to convert raw CSS string to parsed value object
   */
  parse: (value: string) => Record<string, TParsedValue>;
  /**
   * Serializer function to convert parsed value object back to CSS string
   */
  serialize: (value: Record<string, TParsedValue>) => string;
  /**
   * Function key to use for the update styles function
   */
  functionKey: string;
};

export type UsePartialStyleUpdateConfigWithoutKey<TParsedValue> = BaseConfig & {
  /**
   * Parse function to convert raw CSS string to parsed value object
   */
  parse: (value: string) => TParsedValue;
  /**
   * Serializer function to convert parsed value object back to CSS string
   */
  serialize: (value: TParsedValue) => string;
  /**
   * Function key to use for the update styles function
   */
  functionKey?: undefined;
};

export type UsePartialStyleUpdateConfig<TParsedValue> =
  | UsePartialStyleUpdateConfigWithKey<TParsedValue>
  | UsePartialStyleUpdateConfigWithoutKey<TParsedValue>;

function hasConfigFunctionKey<TParsedValue>(
  config: UsePartialStyleUpdateConfig<TParsedValue>,
): config is UsePartialStyleUpdateConfigWithKey<TParsedValue> {
  return config.functionKey !== undefined;
}

export type UsePartialStyleUpdateResult<TParsedValue> = {
  /**
   * Array of processed style items with their IDs and parsed values
   */
  items: ProcessedStyleItem<TParsedValue>[];
  /**
   * Get the first non-null value for a specific sub-property
   */
  getValue: <K extends keyof TParsedValue>(
    subProperty: K,
  ) => TParsedValue[K] | undefined;
  /**
   * Check if a sub-property has mixed values across items
   */
  isMixed: <K extends keyof TParsedValue>(subProperty: K) => boolean;
  /**
   * Get an onChange handler for a specific sub-property
   * that updates all items with the new value
   */
  getOnChange: <K extends keyof TParsedValue>(
    subProperty: K,
  ) => (updatedValue: TParsedValue[K]) => void;
};

/**
 * Generic hook for partial style value updates.
 *
 * This hook encapsulates the common pattern of:
 * 1. Processing style values from both cssCustomProperties and cssProperties
 * 2. Filtering by CSS property type
 * 3. Parsing values into structured objects
 * 4. Providing utilities to read and update individual sub-properties
 *
 * @example
 * ```tsx
 * const { getValue, isMixed, getOnChange } = usePartialStyleUpdate({
 *   values,
 *   cssPropertyTypes: CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow,
 *   parse: parseBoxShadowValue,
 *   serialize: boxShadowParsedValueToString,
 *   updateStyles,
 * });
 *
 * // In render:
 * <BlurControl
 *   value={getValue('blurRadius') || ''}
 *   onChange={getOnChange('blurRadius')}
 *   isMixed={isMixed('blurRadius')}
 * />
 * ```
 */
export function usePartialStyleUpdate<
  TParsedValue extends Record<string, unknown>,
>(
  config: UsePartialStyleUpdateConfigWithKey<TParsedValue>,
): UsePartialStyleUpdateResult<TParsedValue>;

export function usePartialStyleUpdate<
  TParsedValue extends Record<string, unknown>,
>(
  config: UsePartialStyleUpdateConfigWithoutKey<TParsedValue>,
): UsePartialStyleUpdateResult<TParsedValue>;

export function usePartialStyleUpdate<
  TParsedValue extends Record<string, unknown>,
>(
  config: UsePartialStyleUpdateConfig<TParsedValue>,
): UsePartialStyleUpdateResult<TParsedValue> {
  const { values, cssPropertyTypes, updateStyles } = config;

  const allowedTypes = useMemo(
    () =>
      new Set(
        Array.isArray(cssPropertyTypes) ? cssPropertyTypes : [cssPropertyTypes],
      ),
    [cssPropertyTypes],
  );

  const items = useMemo(() => {
    const processedItems: ProcessedStyleItem<TParsedValue>[] = [];

    // Handle cssCustomProperties
    const cssCustomProps =
      values.mode === 'cssCustomProperties' || values.mode === 'mixed'
        ? values.raw.cssCustomProperties
        : {};
    const cssCustomDefs =
      values.mode === 'cssCustomProperties' || values.mode === 'mixed'
        ? values.definitions.cssCustomProperties
        : {};

    Object.entries(cssCustomProps).forEach(([key, value]) => {
      const cssPropertyType = cssCustomDefs[key]?.cssPropertyType;
      if (cssPropertyType && allowedTypes.has(cssPropertyType)) {
        if (hasConfigFunctionKey(config)) {
          const parsedValue = config.parse(value)[config.functionKey];
          processedItems.push({
            styleItemId: key,
            parsedValue,
          });
        } else {
          const parsedValue = config.parse(value);
          processedItems.push({
            styleItemId: key,
            parsedValue,
          });
        }
      }
    });

    // Handle cssProperties
    const cssProps =
      values.mode === 'cssProperties' || values.mode === 'mixed'
        ? values.raw.cssProperties
        : {};
    const cssDefs =
      values.mode === 'cssProperties' || values.mode === 'mixed'
        ? values.definitions.cssProperties
        : {};

    Object.entries(cssProps).forEach(([key, value]) => {
      if (cssDefs[key]) {
        if (hasConfigFunctionKey(config)) {
          const parsedValue = config.parse(value)[config.functionKey];
          processedItems.push({
            styleItemId: key,
            parsedValue,
          });
        } else {
          const parsedValue = config.parse(value);
          processedItems.push({
            styleItemId: key,
            parsedValue,
          });
        }
      }
    });

    return processedItems;
  }, [values, allowedTypes, config]);

  const getValue = useCallback(
    <K extends keyof TParsedValue>(
      subProperty: K,
    ): TParsedValue[K] | undefined => {
      const foundItem = items.find((styleItem) =>
        Boolean(styleItem.parsedValue[subProperty]),
      );
      return foundItem?.parsedValue[subProperty];
    },
    [items],
  );

  const isMixed = useCallback(
    <K extends keyof TParsedValue>(subProperty: K): boolean => {
      let lastViewedValue: TParsedValue[K] | null = null;

      for (const item of items) {
        const currentValue = item.parsedValue[subProperty];

        if (currentValue) {
          if (lastViewedValue !== null && lastViewedValue !== currentValue) {
            return true;
          }

          lastViewedValue = currentValue;
        }
      }

      return false;
    },
    [items],
  );

  const getOnChange = useCallback(
    <K extends keyof TParsedValue>(subProperty: K) =>
      (updatedValue: TParsedValue[K]) => {
        const stylesChanges: Record<string, string> = {};

        items.forEach((styleItem) => {
          const updatedParsedValue = {
            ...styleItem.parsedValue,
            [subProperty]: updatedValue,
          };

          stylesChanges[styleItem.styleItemId] = hasConfigFunctionKey(config)
            ? config.serialize({
                [config.functionKey]: updatedParsedValue,
              })
            : config.serialize(updatedParsedValue);
        });

        updateStyles(stylesChanges);
      },
    [items, config, updateStyles],
  );

  return {
    items,
    getValue,
    isMixed,
    getOnChange,
  };
}
